import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../../shared/design-system/structured-json-form.component';
import {
  SEARCH_OPS_LOCAL_STATUSES,
  SEARCH_OPS_REVIEW_ACTIONS,
  SEARCH_OPS_REVIEW_STATUSES,
  SearchCitationDto,
  SearchLocalProfileDto,
  SearchPropertyListItemDto,
  SearchReviewTaskDto,
  searchOpsNapFields,
  searchOpsReviewEvidenceFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-local',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TableModule,
    RouterLink,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.local.title')"
      [description]="t('growth.searchops.local.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.local.help.title'"
        [descriptionKey]="'growth.searchops.local.help.description'"
        [bulletKeys]="[
          'growth.searchops.local.help.bullets.nap',
          'growth.searchops.local.help.bullets.match',
          'growth.searchops.local.help.bullets.gbp'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/offpage']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.offpage')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/sem']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.sem')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.backlog')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.local.sections.nap') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.local.napHelp.title'"
              [descriptionKey]="'growth.searchops.local.napHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.status')">
            <p-select [options]="statusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="napStatus" appendTo="body" />
          </ain-form-field>
          <ain-structured-json-form [fields]="napFields" [(payloadJson)]="napJson" />
          <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveNap()"></button>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.local.sections.citations') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.local.citationsHelp.title'"
              [descriptionKey]="'growth.searchops.local.citationsHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.local.actions.addCitation')" (click)="openCitation()"></button>
          <p-table [value]="citations()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '40rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.directory') }}</th>
                <th>{{ t('growth.searchops.fields.url') }}</th>
                <th>{{ t('growth.searchops.fields.napMatch') }}</th>
                <th>{{ t('growth.searchops.fields.observedNap') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.directory }}</td>
                <td>{{ row.url }}</td>
                <td>{{ t('growth.searchops.napMatch.' + row.napMatchStatus) }}</td>
                <td>
                  <ain-structured-json-display [fields]="napFields" [payloadJson]="row.napJson" />
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.local.emptyCitations') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.local.sections.reviews') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.local.reviewsHelp.title'"
              [descriptionKey]="'growth.searchops.local.reviewsHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.local.actions.addReview')" (click)="openReview()"></button>
          <p-table [value]="reviews()" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.externalId') }}</th>
                <th>{{ t('growth.searchops.fields.action') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.externalId }}</td>
                <td>{{ t('growth.searchops.reviewAction.' + row.action) }}</td>
                <td>{{ t('growth.searchops.reviewStatus.' + row.status) }}</td>
                <td>
                  @if (!row.taskId) {
                    <button pButton type="button" size="small" [disabled]="busy()" [label]="t('growth.searchops.local.actions.createTask')" (click)="createReviewTask(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.local.emptyReviews') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>

    <p-dialog [(visible)]="citationOpen" [modal]="true" [header]="t('growth.searchops.local.dialogs.citation')" [style]="{ width: '32rem' }" appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.local.citationDialogHelp.title'"
        [descriptionKey]="'growth.searchops.local.citationDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.directory')">
        <input pInputText [(ngModel)]="citationModel.directory" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.url')">
        <input pInputText [(ngModel)]="citationModel.url" />
      </ain-form-field>
      <ain-structured-json-form [fields]="napFields" [(payloadJson)]="citationModel.napJson" />
      <div class="dialog-actions">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveCitation()"></button>
      </div>
    </p-dialog>

    <p-dialog [(visible)]="reviewOpen" [modal]="true" [header]="t('growth.searchops.local.dialogs.review')" [style]="{ width: '32rem' }" appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.local.reviewDialogHelp.title'"
        [descriptionKey]="'growth.searchops.local.reviewDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.externalId')">
        <input pInputText [(ngModel)]="reviewModel.externalId" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.action')">
        <p-select [options]="actionOptions()" optionLabel="label" optionValue="value" [(ngModel)]="reviewModel.action" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.status')">
        <p-select [options]="reviewStatusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="reviewModel.status" appendTo="body" />
      </ain-form-field>
      <ain-structured-json-form [fields]="reviewFields" [(payloadJson)]="reviewModel.evidenceJson" />
      <div class="dialog-actions">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveReview()"></button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .toolbar, .section-title, .dialog-actions { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; margin-bottom: 1rem; }
    .live-badge { margin-inline-start: auto; }
    .card { margin-bottom: 1.25rem; }
    h3 { margin: 0; }
    td { white-space: normal; overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsLocalComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  readonly napFields = searchOpsNapFields;
  readonly reviewFields = searchOpsReviewEvidenceFields;
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly profile = signal<SearchLocalProfileDto | null>(null);
  readonly citations = signal<SearchCitationDto[]>([]);
  readonly reviews = signal<SearchReviewTaskDto[]>([]);
  selectedPropertyId: string | null = null;
  napJson = '{}';
  napStatus = 'draft';
  citationOpen = false;
  reviewOpen = false;
  citationModel = { directory: '', url: '', napJson: '{}' };
  reviewModel = { externalId: '', action: 'reply', status: 'open', evidenceJson: '{}' };
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    this.liveUpdateService.events$
      .pipe(
        filter(event => event.eventType === LiveUpdateEventTypes.growthSearchOpsChanged),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));
    void this.setupLiveSubscription();
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  statusOptions() {
    return SEARCH_OPS_LOCAL_STATUSES.map(value => ({ value, label: this.t('growth.searchops.localStatus.' + value) }));
  }

  actionOptions() {
    return SEARCH_OPS_REVIEW_ACTIONS.map(value => ({ value, label: this.t('growth.searchops.reviewAction.' + value) }));
  }

  reviewStatusOptions() {
    return SEARCH_OPS_REVIEW_STATUSES.map(value => ({ value, label: this.t('growth.searchops.reviewStatus.' + value) }));
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  openCitation(): void {
    this.citationModel = { directory: '', url: '', napJson: '{}' };
    this.citationOpen = true;
  }

  openReview(): void {
    this.reviewModel = { externalId: '', action: 'reply', status: 'open', evidenceJson: '{}' };
    this.reviewOpen = true;
  }

  saveNap(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertLocalProfile({
      searchPropertyId: this.selectedPropertyId,
      napJson: this.napJson,
      status: this.napStatus
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveCitation(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertCitation({
      searchPropertyId: this.selectedPropertyId,
      localProfileId: this.profile()?.id ?? null,
      directory: this.citationModel.directory,
      url: this.citationModel.url,
      napJson: this.citationModel.napJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.citationOpen = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveReview(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertReviewTask({
      searchPropertyId: this.selectedPropertyId,
      localProfileId: this.profile()?.id ?? null,
      externalId: this.reviewModel.externalId,
      action: this.reviewModel.action,
      status: this.reviewModel.status,
      evidenceJson: this.reviewModel.evidenceJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.reviewOpen = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  createReviewTask(row: SearchReviewTaskDto): void {
    this.busy.set(true);
    this.api.createTaskFromReview(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties[0]) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoards();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private loadBoards(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    const propertyId = this.selectedPropertyId;
    forkJoin({
      profile: this.api.getLocalProfile(propertyId),
      citations: this.api.listCitations(propertyId),
      reviews: this.api.listReviewTasks(propertyId)
    }).subscribe({
      next: boards => {
        this.profile.set(boards.profile);
        this.napJson = boards.profile?.napJson ?? '{}';
        this.napStatus = boards.profile?.status ?? 'draft';
        this.citations.set(boards.citations);
        this.reviews.set(boards.reviews);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
